import { isValidTimeZone } from "../domain/business-date";
import type { PromiseEntry, PromiseMateriality } from "../domain/types";

export const PromiseRuleCode = {
  ACTIVE_BECOMES_OVERDUE: "CEO-PROMISE-001",
  RENEGOTIATION_MUST_BE_EARLY: "CEO-PROMISE-002",
  ORIGINAL_DUE_AT_IMMUTABLE: "CEO-PROMISE-003",
  RENEGOTIATION_NOTE_REQUIRED: "CEO-PROMISE-004",
  FULFILLMENT_EVIDENCE_RECOMMENDED: "CEO-PROMISE-005",
  CANCEL_REASON_REQUIRED: "CEO-PROMISE-006",
  BREACH_REASON_REQUIRED: "CEO-PROMISE-007",
  TERMINAL_STATUS_IMMUTABLE: "CEO-PROMISE-008",
  BUSINESS_TIMEZONE_INVALID: "CEO-PROMISE-009",
} as const;

export type PromiseRuleCode = (typeof PromiseRuleCode)[keyof typeof PromiseRuleCode];

export interface PromiseRuleIssue {
  code: PromiseRuleCode;
  severity: "warning" | "blocking";
  path: string;
  rule: string;
  message: string;
}

export interface PromiseRuleResult {
  promise: PromiseEntry;
  issues: PromiseRuleIssue[];
}

const TERMINAL = new Set<PromiseEntry["promiseStatus"]>([
  "fulfilled",
  "cancelled",
  "breached",
  "archived",
]);

function issue(
  code: PromiseRuleCode,
  path: string,
  message: string,
  severity: PromiseRuleIssue["severity"] = "blocking",
): PromiseRuleIssue {
  return {
    code,
    severity,
    path,
    rule: "承诺状态变化必须保留原始截止语义、原因和历史，不得静默覆盖。",
    message,
  };
}

function history(
  action: PromiseEntry["history"][number]["action"],
  occurredAt: string,
  note?: string,
  previousDueAt?: string,
  nextDueAt?: string,
): PromiseEntry["history"][number] {
  return {
    id: crypto.randomUUID(),
    action,
    occurredAt,
    ...(note ? { note } : {}),
    ...(previousDueAt ? { previousDueAt } : {}),
    ...(nextDueAt ? { nextDueAt } : {}),
  };
}

function terminalIssue(entry: PromiseEntry): PromiseRuleIssue[] {
  return TERMINAL.has(entry.promiseStatus)
    ? [
        issue(
          PromiseRuleCode.TERMINAL_STATUS_IMMUTABLE,
          "promiseStatus",
          "终态承诺不能直接改写；需要新建后续承诺或保留审计修订。",
        ),
      ]
    : [];
}

export function evaluatePromiseDueStatus(
  entry: PromiseEntry,
  now = new Date().toISOString(),
): PromiseRuleResult {
  if (!isValidTimeZone(entry.businessTimezone)) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.BUSINESS_TIMEZONE_INVALID,
          "businessTimezone",
          "承诺的业务时区无效。",
        ),
      ],
    };
  }
  if (
    (entry.promiseStatus === "active" ||
      entry.promiseStatus === "renegotiation_needed" ||
      entry.promiseStatus === "renegotiated") &&
    new Date(entry.dueAt).getTime() < new Date(now).getTime()
  ) {
    return {
      promise: {
        ...entry,
        status: "overdue",
        promiseStatus: "overdue",
        updatedAt: now,
        history: [...entry.history, history("overdue", now)],
      },
      issues: [
        issue(
          PromiseRuleCode.ACTIVE_BECOMES_OVERDUE,
          "promiseStatus",
          "承诺已超过原截止时间，状态更新为 overdue；这不等于 breached。",
          "warning",
        ),
      ],
    };
  }
  return { promise: entry, issues: [] };
}

export function requestPromiseRenegotiation(
  entry: PromiseEntry,
  note: string,
  now = new Date().toISOString(),
): PromiseRuleResult {
  const terminalIssues = terminalIssue(entry);
  if (terminalIssues.length) return { promise: entry, issues: terminalIssues };
  if (new Date(now).getTime() >= new Date(entry.dueAt).getTime()) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.RENEGOTIATION_MUST_BE_EARLY,
          "dueAt",
          "截止后不能描述为提前重谈；请先处理逾期事实。",
        ),
      ],
    };
  }
  if (!note.trim()) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.RENEGOTIATION_NOTE_REQUIRED,
          "renegotiationNote",
          "提前重谈需要保存原因或说明。",
        ),
      ],
    };
  }
  return {
    promise: {
      ...entry,
      promiseStatus: "renegotiation_needed",
      renegotiationNote: note.trim(),
      updatedAt: now,
      history: [...entry.history, history("renegotiation_requested", now, note.trim())],
    },
    issues: [],
  };
}

export function renegotiatePromise(
  entry: PromiseEntry,
  nextDueAt: string,
  note: string,
  now = new Date().toISOString(),
): PromiseRuleResult {
  const terminalIssues = terminalIssue(entry);
  if (terminalIssues.length) return { promise: entry, issues: terminalIssues };
  if (!note.trim()) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.RENEGOTIATION_NOTE_REQUIRED,
          "renegotiationNote",
          "完成重谈必须保留双方确认后的说明。",
        ),
      ],
    };
  }
  if (
    new Date(nextDueAt).getTime() <= new Date(now).getTime() ||
    Number.isNaN(new Date(nextDueAt).getTime())
  ) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.ORIGINAL_DUE_AT_IMMUTABLE,
          "dueAt",
          "新的截止时间必须晚于当前时间，且原始截止时间会被永久保留。",
        ),
      ],
    };
  }
  return {
    promise: {
      ...entry,
      promiseStatus: "renegotiated",
      dueAt: nextDueAt,
      renegotiationNote: note.trim(),
      updatedAt: now,
      history: [
        ...entry.history,
        history("renegotiated", now, note.trim(), entry.dueAt, nextDueAt),
      ],
    },
    issues: [],
  };
}

export function fulfillPromise(
  entry: PromiseEntry,
  evidence: string,
  now = new Date().toISOString(),
): PromiseRuleResult {
  const terminalIssues = terminalIssue(entry);
  if (terminalIssues.length) return { promise: entry, issues: terminalIssues };
  const normalized = evidence.trim();
  return {
    promise: {
      ...entry,
      status: "completed",
      promiseStatus: "fulfilled",
      ...(normalized ? { fulfillmentEvidence: normalized } : {}),
      updatedAt: now,
      history: [...entry.history, history("fulfilled", now, normalized || undefined)],
    },
    issues: normalized
      ? []
      : [
          issue(
            PromiseRuleCode.FULFILLMENT_EVIDENCE_RECOMMENDED,
            "fulfillmentEvidence",
            "已允许标记兑现；建议补充最小可观察证据。",
            "warning",
          ),
        ],
  };
}

export function cancelPromise(
  entry: PromiseEntry,
  reason: string,
  now = new Date().toISOString(),
): PromiseRuleResult {
  const terminalIssues = terminalIssue(entry);
  if (terminalIssues.length) return { promise: entry, issues: terminalIssues };
  if (!reason.trim()) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.CANCEL_REASON_REQUIRED,
          "cancellationReason",
          "取消承诺必须记录原因。",
        ),
      ],
    };
  }
  return {
    promise: {
      ...entry,
      status: "cancelled",
      promiseStatus: "cancelled",
      cancellationReason: reason.trim(),
      updatedAt: now,
      history: [...entry.history, history("cancelled", now, reason.trim())],
    },
    issues: [],
  };
}

export function markPromiseBreached(
  entry: PromiseEntry,
  reason: string,
  now = new Date().toISOString(),
): PromiseRuleResult {
  if (entry.promiseStatus === "fulfilled" || entry.promiseStatus === "cancelled") {
    return { promise: entry, issues: terminalIssue(entry) };
  }
  if (!reason.trim()) {
    return {
      promise: entry,
      issues: [
        issue(
          PromiseRuleCode.BREACH_REASON_REQUIRED,
          "breachReason",
          "标记 breached 必须说明与普通 overdue 不同的信任损害事实。",
        ),
      ],
    };
  }
  return {
    promise: {
      ...entry,
      status: "breached",
      promiseStatus: "breached",
      breachReason: reason.trim(),
      updatedAt: now,
      history: [...entry.history, history("breached", now, reason.trim())],
    },
    issues: [],
  };
}

const MATERIALITY_WEIGHT: Record<PromiseMateriality, number> = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export function promiseAttentionRank(entry: PromiseEntry, now = new Date().toISOString()): number {
  if (entry.promiseStatus === "fulfilled" || entry.promiseStatus === "archived") return -1;
  const overdue = new Date(entry.dueAt).getTime() < new Date(now).getTime();
  const dueWithin24h =
    new Date(entry.dueAt).getTime() >= new Date(now).getTime() &&
    new Date(entry.dueAt).getTime() - new Date(now).getTime() <= 24 * 60 * 60 * 1000;
  if (overdue && entry.materiality === "critical") return 100;
  if (overdue && entry.materiality === "high") return 90;
  if (overdue) return 80 + MATERIALITY_WEIGHT[entry.materiality];
  if (dueWithin24h) return 60 + MATERIALITY_WEIGHT[entry.materiality];
  if (entry.pinned) return 50;
  return MATERIALITY_WEIGHT[entry.materiality];
}
